import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { WIZARD_PATHS, backUrlFor, sessionIdFromLocation } from '../lib/wizardRoutes';

export function Success() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user } = useSession();
  const { user: authUser } = useAuth();
  const [publishing, setPublishing] = useState(true);
  const [blogUrl, setBlogUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedPublish, setHasAttemptedPublish] = useState(false);
  const [publishTimeout, setPublishTimeout] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [countdown, setCountdown] = useState(7);
  const urlSessionId = sessionIdFromLocation(location) ?? session?.id ?? null;

  // Actuele waarde van `publishing` voor de timeout (die anders een verouderde closure leest).
  const publishingRef = useRef(publishing);
  publishingRef.current = publishing;

  useEffect(() => {
    const sessionId = urlSessionId;

    console.log('Success screen loaded with:', { sessionId, hasAttemptedPublish });

    if (!sessionId) {
      console.error('Success screen: No sessionId provided');
      setError('Sessie niet gevonden. Ga terug naar preview en probeer opnieuw.');
      setPublishing(false);
      return;
    }

    if (!hasAttemptedPublish) {
      setHasAttemptedPublish(true);
      loadSessionAndPublish(sessionId);
    }

    // Set a timeout to prevent infinite spinner
    const timeout = setTimeout(() => {
      if (publishingRef.current) {
        console.warn('Publishing timeout reached');
        setPublishTimeout(true);
        setPublishing(false);
        setError('Het publiceren duurt langer dan verwacht. Probeer het opnieuw of neem contact op met support.');
      }
    }, 60000); // 60 seconds timeout

    return () => clearTimeout(timeout);
  }, [hasAttemptedPublish, urlSessionId]);

  useEffect(() => {
    if (!publishing && !error && blogUrl && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            navigate('/my-blogs');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [publishing, error, blogUrl, countdown, navigate]);

  const loadSessionAndPublish = async (sessionId: string) => {
    try {
      console.log('Loading session data for publishing:', sessionId);
      setPublishing(true);
      setError(null);

      // Load session data directly from database
      const { data: sessionRecord, error: sessionError } = await supabase
        .from('sessions')
        .select('id, user_id, selected_title, selected_intro, selected_image_url, article_content')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionRecord) {
        console.error('Error loading session:', sessionError);
        throw new Error('Sessie niet gevonden');
      }

      if (!sessionRecord.user_id) {
        console.error('Session has no user_id:', sessionRecord);
        throw new Error('Sessie heeft geen gebruiker gekoppeld');
      }

      console.log('Session loaded successfully:', {
        id: sessionRecord.id,
        userId: sessionRecord.user_id,
        hasTitle: !!sessionRecord.selected_title,
        hasIntro: !!sessionRecord.selected_intro,
        hasImage: !!sessionRecord.selected_image_url,
        hasContent: !!sessionRecord.article_content
      });

      setSessionData(sessionRecord);

      // Now publish with the user_id from the session
      await publishBlog(sessionRecord.id, sessionRecord.user_id);
    } catch (err: any) {
      console.error('Error in loadSessionAndPublish:', err);
      setError(err.message || 'Er ging iets mis bij het laden van de sessie');
      setPublishing(false);
    }
  };

  const publishBlog = async (sessionId: string, userId: string) => {
    try {
      console.log('Starting publish blog for session:', sessionId, 'user:', userId);
      setPublishing(true);
      setError(null);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/publish-blog`;
      console.log('Calling publish API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_id: userId,
        }),
      });

      console.log('Publish API response status:', response.status);
      const data = await response.json();
      console.log('Publish API response data:', data);

      if (!response.ok) {
        // Provide more specific error messages
        if (response.status === 400 && data.error?.includes('username')) {
          throw new Error('Je hebt nog geen gebruikersnaam ingesteld. Ga naar je dashboard om een gebruikersnaam aan te maken.');
        }
        throw new Error(data.error || `Publicatie mislukt (${response.status})`);
      }

      if (data.blog_url) {
        console.log('Blog published successfully:', data.blog_url);
        setBlogUrl(data.blog_url);
      } else {
        console.warn('No blog URL returned from API');
        setError('Blog gepubliceerd maar geen URL ontvangen.');
      }
    } catch (err: any) {
      console.error('Error publishing blog:', err);
      setError(err.message || 'Er ging iets mis bij het publiceren van je blog');
    } finally {
      setPublishing(false);
    }
  };

  if (publishing) {
    return (
      <Layout maxWidth="lg" showDashboardHeader={true} showResetButton={false}>
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <Loader2 className="w-16 h-16 text-[#FF6B35] animate-spin" />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-[#231F20]">
              Je blog wordt gepubliceerd...
            </h1>
            <p className="text-lg text-gray-600">
              Even geduld, we zetten je blog online
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout maxWidth="lg" showDashboardHeader={true} showResetButton={false}>
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-16 h-16 text-red-600" />
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-[#231F20]">
              Er ging iets mis
            </h1>
            <p className="text-lg text-gray-600">
              {error}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                const back = backUrlFor(WIZARD_PATHS.success, urlSessionId);
                if (back) navigate(back);
              }}
              className="btn-secondary"
            >
              Terug naar preview
            </button>
            <button
              onClick={() => {
                if (!urlSessionId) return;
                setHasAttemptedPublish(false);
                setError(null);
                setPublishing(true);
                setPublishTimeout(false);
              }}
              disabled={!urlSessionId}
              className="btn-primary"
            >
              Probeer opnieuw
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout maxWidth="lg" showDashboardHeader={true} showResetButton={false}>
      <div className="space-y-8 text-center">
        <div className="flex justify-center">
          <div className="relative w-24 h-24">
            <svg className="absolute inset-0 w-24 h-24 -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="44"
                stroke="#e5e7eb"
                strokeWidth="4"
                fill="none"
              />
              <circle
                cx="48"
                cy="48"
                r="44"
                stroke="#10b981"
                strokeWidth="4"
                fill="none"
                strokeDasharray={276.46}
                strokeDashoffset={276.46 * (1 - countdown / 7)}
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold text-[#231F20]">
            Je blog is live!
          </h1>
          <p className="text-lg text-gray-600">
            Je blog is nu online en deelbaar
          </p>
        </div>

        {(sessionData?.selected_title || session?.selected_title) && (
          <div className="card text-left max-w-xl mx-auto">
            {(sessionData?.selected_image_url || session?.selected_image_url) && (
              <img
                src={sessionData?.selected_image_url || session?.selected_image_url}
                alt="Selected"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
            <h2 className="text-xl font-bold text-[#231F20] mb-2">
              {sessionData?.selected_title || session?.selected_title}
            </h2>
            {(sessionData?.selected_intro || session?.selected_intro) && (
              <p className="text-gray-600 text-sm line-clamp-3">
                {sessionData?.selected_intro || session?.selected_intro}
              </p>
            )}
          </div>
        )}

        <div className="space-y-4">
          <p className="text-gray-600">
            Je wordt doorgestuurd naar je blog overzicht waar je de URL kunt kopiëren, een campagne kunt maken, of verdere actie kunt ondernemen.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/my-blogs')}
              className="btn-primary"
            >
              Nu naar mijn blogs
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
